import React, { useContext, useEffect, useState } from 'react';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import { PartPayload, WizardContext } from 'compositions/Wizard/WizardContext';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGetApprovalContacts } from 'hooks/useGetApprovalContacts';
import { GetMacroApprovalLimitsParams, MacroApprovalLimits, useGetMacroApprovalLimits } from 'hooks/useGetMacroApprovalLimits';
import { useGetPrices } from 'hooks/useGetPrices';
import { useWithinSpendingLimit } from 'hooks/useWithinSpendingLimit';
import { LocationCore } from 'types/UFOTypes';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { CentralProviderContext, CentralProviderState } from '@michelin/central-provider';
import LimitsMessages from './LimitsMessages';

interface LimitsProps {
  provider: LocationCore;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  fleet_id: string;
  orderApprovalLimit?: string;
  parts: PartPayload[];
  commonPricesParams: CommonPricesParamsType;
}

export function Limits(props: LimitsProps) {
  /* --- PROPS --- */
  const { provider, fleet_id: fleetId, orderApprovalLimit, parts, commonPricesParams } = props;

  /* --- STATE ---  */
  const [error, setError] = useState(false);
  const [approvalLimits, setApprovalLimits] = useState<MacroApprovalLimits|null>();
  const [hasContacts, setHasContacts] = useState<number>();
  const [hasApprovalLimits, setHasApprovalLimits] = useState<boolean>();

  /* --- CONTEXTS --- */
  const { setAuthorized, setDisableCreateOrder } = useContext(WizardContext);
  const { setAuthorized: setAuthorizedOrderDetailContext, setActionButtonLoading } = useContext(OrderDetailContext);
  const { selectedGrant } = useContext<CentralProviderState>(CentralProviderContext);
  const isAdmin = selectedGrant?.role?.permissions?.fleet_admin_permissions?.["Unlimited Spending Limit"] as boolean ?? false;

  /* --- HOOKS --- */
  const isDealer = useCurrentLocationIsDealer() ?? false;
  const [macroApprovalLimitsPayload, setMacroApprovalLimitsPayload] = useState<GetMacroApprovalLimitsParams>();
  const getMacroApprovalLimits = useGetMacroApprovalLimits(macroApprovalLimitsPayload);
  const pricesResponse = useGetPrices({
    ...commonPricesParams,
    parts: parts
      .filter((p) => !p.manual_pricing)

      .map((p) => ({ mspn: getMspn(p) as string, qty: p.quantity, is_pql: p.ispql })),
  }); // filter pql manual prices
  const prices = pricesResponse?.data?.parts;
  const pqlPrices = pricesResponse?.data?.pqlParts;
  const dealerCanCreateOrder = useWithinSpendingLimit(
    !isDealer || !fleetId || parts.length === 0
      ? null
      : {
          provider_id: `${provider.customer_number}`,
          fleet_id: `${fleetId}`,
          country_code: provider.customer_country || 'US',
          state: provider.customer_state || null,
          city: provider.customer_city || null,
          zip_code: provider.customer_zip || null,
          order_limit: Number(orderApprovalLimit) || null,
          parts: parts.map((p) => ({
            mspn: getMspn(p) as string,
            qty: p.quantity || Number(prices?.find((pp) => getMspn(pp) === p.part_id)?.qty),
            is_pql: p.ispql || false,
            amount:
              p.final_invoice_price ??
              (pqlPrices
                ? Number(pqlPrices.find((pp) => getMspn(pp) === p.part_id)?.totalAmount || 0) || undefined
                : undefined), // only for pql
          })),
        },
  );
  // Get total price
  let totalInvoicePrice = 0;
  if (prices) {
    prices.forEach((fp) => {
      totalInvoicePrice += Number.parseFloat(fp.final_invoice_price);
    });
  }
  if (pqlPrices) {
    pqlPrices.forEach((fp) => {
      totalInvoicePrice += fp.totalAmount;
    });
  }
  parts.forEach((op) => {
    if (op.manual_pricing) {
      totalInvoicePrice += Number(op.invoice_price) * op.quantity;
    }
  });
  const contactsResponse = useGetApprovalContacts(
    pricesResponse.isLoading
      ? null
      : {
          fleet_id: `${fleetId}`,
          amount: totalInvoicePrice > 0 ? totalInvoicePrice : 0.01,
        },
  );

  /* --- EFFECTS --- */
  useEffect(() => {
    if (setActionButtonLoading)
      setActionButtonLoading(
        getMacroApprovalLimits.isLoading || dealerCanCreateOrder.isLoading,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMacroApprovalLimits.isLoading, dealerCanCreateOrder]);

  useEffect(() => {
    if (setAuthorizedOrderDetailContext) {
      if (!error && hasApprovalLimits) {
        setAuthorizedOrderDetailContext(true);
        return;
      }
      if (!error && !hasApprovalLimits && hasContacts) {
        setAuthorizedOrderDetailContext(true);
        return;
      }
      setAuthorizedOrderDetailContext(false);
    }
    if (setAuthorized !== undefined) {
      if (!error && hasApprovalLimits) {
        setAuthorized(true);
        return;
      }
      if (!error && !hasApprovalLimits && hasContacts) {
        setAuthorized(true);
        return;
      }
      setAuthorized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, hasApprovalLimits, hasContacts]);


  // check auto-approval for dealers
  useEffect(() => {
    if (isDealer && !dealerCanCreateOrder.isLoading && dealerCanCreateOrder.data) {
      setHasApprovalLimits(dealerCanCreateOrder.data.within_spending_limit && dealerCanCreateOrder.data.urban_admin_allow_preapproved_txs);
    }
    if (dealerCanCreateOrder.error && !error) setError(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerCanCreateOrder]);



  // check auto-approval for fleets
  useEffect(() => {
    if (!isDealer && (approvalLimits || orderApprovalLimit)) {
      if (isAdmin) setHasApprovalLimits(true);
      else if (orderApprovalLimit && totalInvoicePrice <= Number(orderApprovalLimit)) setHasApprovalLimits(true);
      else if (approvalLimits?.urban_admin_preapproved_txs_no_limits && approvalLimits.urban_admin_allow_preapproved_txs) setHasApprovalLimits(true);
      else if (approvalLimits) setHasApprovalLimits(totalInvoicePrice <= Number(approvalLimits.urban_admin_preapproved_txs_limit_amount) && approvalLimits.urban_admin_allow_preapproved_txs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderApprovalLimit, totalInvoicePrice, approvalLimits]);

  const updateFleetLimits = () => {
    setMacroApprovalLimitsPayload({ fleet_id: `${fleetId}` });
  };


  // Update approvalLimits
  useEffect(() => {
    if (getMacroApprovalLimits.data) setApprovalLimits(getMacroApprovalLimits.data);
    if (getMacroApprovalLimits.data?.message) setError(true);
  }, [getMacroApprovalLimits.data]);

  useEffect(() => {
    if (
      !isDealer &&
      approvalLimits === undefined &&
      !getMacroApprovalLimits.isLoading &&
      !error
    ) {
      updateFleetLimits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, provider, isDealer, pricesResponse]);

  useEffect(() => {
      if (setDisableCreateOrder) setDisableCreateOrder(!error && !hasApprovalLimits && !hasContacts);
  },[setDisableCreateOrder, error, hasApprovalLimits, hasContacts]);

  /* --- Pre-render logic --- */
  if (contactsResponse.error && !error) setError(true);

  if (
    hasContacts === undefined &&
    !error &&
    (!hasApprovalLimits || totalInvoicePrice === 0) &&
    !contactsResponse.isLoading
  ) {
    if (Array.isArray(contactsResponse.data)) {
      setHasContacts(contactsResponse.data.length);
    }

    if (contactsResponse.data === undefined) {
      setHasContacts(0);
    } else if ((contactsResponse.data !== null && !Array.isArray(contactsResponse.data)) || contactsResponse.error) {
      setError(true);
    }
  }

  /* render */
  if (
    getMacroApprovalLimits.isLoading ||
    dealerCanCreateOrder.isLoading ||
    contactsResponse.isLoading ||
    hasApprovalLimits === undefined ||
    hasContacts === undefined
  ) {
    return null;
  }
  return (
    <LimitsMessages
      hasApprovalLimits={hasApprovalLimits}
      hasContacts={hasContacts}
      isDealer={isDealer}
      getLimitsError={error}
      setGetLimitsError={(e) => {
        setApprovalLimits(undefined);
        setHasContacts(undefined);
        setError(e);
      }}
    />
  );
}
